
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';
import { Loader2, Trophy } from 'lucide-react';

interface LeaderboardEntry {
  user_id: string;
  subject: string;
  score: number;
  rank: number;
  date: string;
  isCurrentUser: boolean;
}

interface LeaderboardProps {
  subject: string;
  performanceData: any[];
}

const LeaderboardChart: React.FC<LeaderboardProps> = ({ subject, performanceData }) => {
  const { data: leaderboardData, isLoading } = useQuery({
    queryKey: ['leaderboard', subject],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('performance_history')
        .select('user_id, subject, score, created_at')
        .eq('subject', subject)
        .order('score', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Process data to include ranks and generate unique keys
      const processedData = data.map((entry, index) => ({
        ...entry,
        rank: index + 1,
        isCurrentUser: entry.user_id === user.user.id,
        date: new Date(entry.created_at).toLocaleDateString(),
        // Add an index to make each entry unique even if date is the same
        uniqueId: `${entry.user_id}-${index}`
      }));

      return processedData;
    },
    enabled: !!subject,
  });

  const radarData = React.useMemo(() => {
    if (!performanceData) return [];

    const last5Attempts = performanceData
      .filter(entry => entry.subject === subject)
      .slice(-5)
      .map((entry, index) => ({
        attempt: `Attempt ${index + 1}`,
        score: entry.score || 0,
        accuracy: (entry.correct_answers / entry.total_questions) * 100 || 0,
        rank: entry.rank || 10,
      }));

    return last5Attempts;
  }, [performanceData, subject]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          {subject} Leaderboard
        </CardTitle>
        <CardDescription>
          Track your rank progression and performance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Leaderboard Table */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Top Performers</h3>
            <div className="space-y-2">
              {leaderboardData?.map((entry: LeaderboardEntry & { uniqueId: string }) => (
                <div
                  key={entry.uniqueId}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    entry.isCurrentUser
                      ? "bg-purple-100 dark:bg-purple-900"
                      : "bg-gray-50 dark:bg-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-bold min-w-[2rem]">#{entry.rank}</span>
                    <span>{entry.isCurrentUser ? "You" : "Anonymous"}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{entry.score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Radar Chart */}
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <PolarGrid />
                <PolarAngleAxis dataKey="attempt" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke="#8B5CF6"
                  fill="#8B5CF6"
                  fillOpacity={0.6}
                />
                <Radar
                  name="Accuracy"
                  dataKey="accuracy"
                  stroke="#10B981"
                  fill="#10B981"
                  fillOpacity={0.6}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaderboardChart;
